// app/features/ai-workflows/services_and_hooks/personalizedUseCasesService.ts
// app/features/personalized-use-cases/services_and_hooks/personalizedUseCasesService.ts

import { Database } from "@/types/supabase";

import {
  PersonalizedUseCase,
  PersonalizedUseCaseCreationInput,
  PersonalizedUseCaseRestartResponse,
  PersonalizedUseCaseWithToolsApiResponse,
  RestartCheckpoint,
} from "../personalized-use-case.interface";

// Use Supabase types
type UseCase = Database["public"]["Tables"]["use_cases"]["Row"];

const API_BASE_PATH = "/api/personalized-use-cases";

// v2

export const personalizedUseCasesService = {
  // Kept functions that make `fetch` calls to the Next.js API
  fetchPersonalizedUseCases: async (
    userId: string,
    signal?: AbortSignal,
  ): Promise<PersonalizedUseCase[]> => {
    console.log(
      `[FE Service] Fetching personalized use cases for user ${userId} from: /api/users/${userId}/personalized-use-cases`,
    );
    const response = await fetch(
      `/api/users/${userId}/personalized-use-cases`,
      { signal },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error ||
          `Failed to fetch personalized use cases: ${response.statusText}`,
      );
    }
    // The API returns { useCases: [...] }, so we need to adapt.
    const data = await response.json();
    return data.useCases || [];
  },

  fetchPersonalizedUseCaseByIdWithTools: async (
    id: string,
  ): Promise<PersonalizedUseCaseWithToolsApiResponse> => {
    console.log(
      `[FE Service] Fetching personalized use case by ID with tools: ${id} from: ${API_BASE_PATH}?id=${id}`,
    );
    const response = await fetch(
      `${API_BASE_PATH}?id=${encodeURIComponent(id)}`,
    );

    if (!response.ok) {
      if (response.status === 404) {
        console.warn(`[FE Service] Use case ${id} not found (404).`);
        // Return the full expected structure, even on 404, with nulls/empty where appropriate
        return {
          personalizedUseCase: null,
          toolsData: {},
          error: "Personalized use case not found",
        };
      }
      // Try to parse error JSON, otherwise use status text
      const errorData = await response
        .json()
        .catch(() => ({ error: `HTTP error! status: ${response.status}` }));
      throw new Error(
        errorData.error ||
          `Failed to fetch personalized use case ${id}: ${response.statusText}`,
      );
    }

    const data: PersonalizedUseCaseWithToolsApiResponse = await response.json();

    // Ensure the response conforms to the interface, providing defaults if parts are missing
    return {
      personalizedUseCase: data.personalizedUseCase || null,
      toolsData: data.toolsData || {},
      error: data.error, // Pass through error if API includes it in success response
    };
  },

  // This is the function called by useGenerateUseCaseMutationHook
  // It POSTs to your Next.js API route `/api/personalized-use-cases/create`
  generatePersonalizedUseCase: async (
    input: PersonalizedUseCaseCreationInput,
  ): Promise<PersonalizedUseCase> => {
    // Expecting the created PersonalizedUseCase record
    console.log(
      `[FE Service] Initiating generation via POST to: ${API_BASE_PATH}/create with input:`,
      input,
    );
    const response = await fetch(`${API_BASE_PATH}/create`, {
      // Fixed: Added /create to the path
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    });

    const responseData = await response.json(); // Assuming API route returns the created PersonalizedUseCase

    if (!response.ok) {
      console.error("[FE Service - generate] API error:", responseData);
      throw new Error(
        responseData.error ||
          responseData.message ||
          `Failed to generate personalized use case: ${response.statusText}`,
      );
    }
    if (!responseData.id) {
      // Check if the response from Next.js API has the ID
      throw new Error(
        "Generation initiated but API did not return a valid use case ID.",
      );
    }
    return responseData as PersonalizedUseCase; // This is the initial DB record
  },

  // Restart a failed or incomplete use case generation from a specific checkpoint
  restartUseCaseGeneration: async (
    useCaseId: string,
    checkpoint: RestartCheckpoint,
  ): Promise<PersonalizedUseCaseRestartResponse> => {
    console.log(
      `[FE Service] Restarting use case ${useCaseId} from checkpoint: ${checkpoint}`,
    );

    const response = await fetch(`${API_BASE_PATH}/restart`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        useCaseId,
        checkpoint,
      }),
    });

    const responseData: PersonalizedUseCaseRestartResponse =
      await response.json();

    if (!response.ok) {
      console.error("[FE Service - restart] API error:", responseData);
      throw new Error(
        responseData.error ||
          `Failed to restart use case generation: ${response.statusText}`,
      );
    }

    console.log(
      `[FE Service] Successfully initiated restart for use case ${useCaseId}:`,
      responseData,
    );
    return responseData;
  },
};

// Export individual functions if usePersonalizedUseCasesQueryHook imports them directly
export const {
  fetchPersonalizedUseCases,
  generatePersonalizedUseCase,
  restartUseCaseGeneration,
} = personalizedUseCasesService;

// Export the renamed function for fetching by ID with tools
export const fetchPersonalizedUseCaseByIdWithTools =
  personalizedUseCasesService.fetchPersonalizedUseCaseByIdWithTools;
